import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, Plus, Trash2, ArrowLeft } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { PhoneInput } from "../../components/PhoneInput";
import { SearchableSelect } from "../../components/SearchableSelect";
import { DateTimePicker } from "../../components/DateTimePicker";
import { VenueDialog } from "../../components/VenueDialog";

/**
 * Shared form for Create and Edit events.
 * mode: "create" | "edit"
 * eventId: for edit mode
 */
export default function EventFormPage({ mode = "create" }) {
    const { t } = useTranslation();
    const { id: routeId } = useParams();
    const eventId = mode === "edit" ? routeId : null;
    const navigate = useNavigate();
    const { profile } = useAuth();
    const [loading, setLoading] = useState(mode === "edit");
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState([]);
    const [venues, setVenues] = useState([]);
    const [artists, setArtists] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const fileRef = useRef(null);
    const [venueDialogOpen, setVenueDialogOpen] = useState(false);
    const [venueDialogPrefill, setVenueDialogPrefill] = useState("");

    const [f, setF] = useState({
        title: "",
        description: "",
        featured_image_url: null,
        venue_id: "",
        start_datetime: "",
        end_datetime: "",
        contact_phone_code: "+1",
        contact_phone: "",
        contact_email: "",
        category_ids: [],
        artist_ids: [],
        ticket_types: [
            {
                id: null,
                name: "",
                price: 0,
                total_quantity: 100,
                min_per_order: 1,
                max_per_order: 10,
                sale_start_datetime: "",
                sale_end_datetime: "",
                description: "",
            },
        ],
    });

    const [eventCountryId, setEventCountryId] = useState(null);
    const [eventCurrencyCode, setEventCurrencyCode] = useState(null);

    const organizerCountryId = profile?.organizer_country?.id || profile?.organizer_country_id;
    // In EDIT mode, venues/currency follow the event's country (locked at creation).
    // In CREATE mode, they follow the organizer's country.
    const effectiveCountryId = mode === "edit" ? eventCountryId : organizerCountryId;
    const currencyCode = (mode === "edit" ? eventCurrencyCode : profile?.organizer_country?.currency_code) || "USD";
    const isAdmin = !!profile?.is_admin;

    // Load static lists (categories + artists)
    useEffect(() => {
        (async () => {
            const [c, a] = await Promise.all([
                api.get("/api/categories"),
                api.get("/api/artists"),
            ]);
            setCategories(c.data || []);
            setArtists(a.data || []);
        })();
    }, []);

    // Load venues filtered by effectiveCountryId
    useEffect(() => {
        if (!effectiveCountryId) return;
        api.get("/api/venues", { params: { country_id: effectiveCountryId } })
            .then(({ data }) => setVenues(data || []));
    }, [effectiveCountryId]);

    // Load event for edit mode
    useEffect(() => {
        if (mode !== "edit" || !eventId) return;
        (async () => {
            try {
                const { data } = await api.get(`/api/organizer/events/${eventId}/manage`);
                setEventCountryId(data.country_id || null);
                setEventCurrencyCode(data.country?.currency_code || null);
                setF({
                    title: data.title || "",
                    description: data.description || "",
                    featured_image_url: data.featured_image_url,
                    venue_id: data.venue_id || "",
                    start_datetime: data.start_datetime ? data.start_datetime.slice(0, 16) : "",
                    end_datetime: data.end_datetime ? data.end_datetime.slice(0, 16) : "",
                    contact_phone_code: data.contact_phone_code || "+1",
                    contact_phone: data.contact_phone || "",
                    contact_email: data.contact_email || "",
                    category_ids: (data.categories || []).map((c) => c.id),
                    artist_ids: (data.artists || []).map((a) => a.id),
                    ticket_types: (data.ticket_types || []).map((tt) => ({
                        id: tt.id,
                        name: tt.name,
                        price: tt.price,
                        total_quantity: tt.total_quantity,
                        min_per_order: tt.min_per_order,
                        max_per_order: tt.max_per_order,
                        sale_start_datetime: tt.sale_start_datetime ? tt.sale_start_datetime.slice(0, 16) : "",
                        sale_end_datetime: tt.sale_end_datetime ? tt.sale_end_datetime.slice(0, 16) : "",
                        description: tt.description || "",
                        sold_count: tt.sold_count || 0,
                    })),
                });
            } finally {
                setLoading(false);
            }
        })();
    }, [mode, eventId]);

    const uploadImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post("/api/uploads/event-image", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setF({ ...f, featured_image_url: data.url });
            toast.success(t("common.success"));
        } catch {
            toast.error(t("common.error"));
        }
    };

    const addTicketType = () => {
        setF({
            ...f,
            ticket_types: [
                ...f.ticket_types,
                {
                    id: null,
                    name: "",
                    price: 0,
                    total_quantity: 100,
                    min_per_order: 1,
                    max_per_order: 10,
                    sale_start_datetime: "",
                    sale_end_datetime: "",
                    description: "",
                },
            ],
        });
    };

    const removeTicketType = (idx) => {
        // Only remove if it's a new unsaved ticket type (no id)
        const tt = f.ticket_types[idx];
        if (tt.id) {
            toast.error("Cannot remove existing ticket types");
            return;
        }
        setF({ ...f, ticket_types: f.ticket_types.filter((_, i) => i !== idx) });
    };

    const updateTicket = (idx, patch) => {
        const list = [...f.ticket_types];
        list[idx] = { ...list[idx], ...patch };
        setF({ ...f, ticket_types: list });
    };

    const createVenue = async (name) => {
        setVenueDialogOpen(true);
        setVenueDialogPrefill(name || "");
    };
    const onVenueCreated = (data) => {
        setVenues((prev) => [...prev, data]);
        setF((prev) => ({ ...prev, venue_id: data.id }));
    };

    const createArtist = async (name) => {
        try {
            const endpoint = isAdmin ? "/api/admin/artists" : "/api/organizer/artists";
            const { data } = await api.post(endpoint, { name });
            setArtists([...artists, data]);
            setF({ ...f, artist_ids: [...f.artist_ids, data.id] });
        } catch {}
    };

    const save = async () => {
        if (!f.title || !f.venue_id || !f.start_datetime || !f.end_datetime) {
            toast.error(t("auth.required"));
            return;
        }
        // Date order validation: event end must be strictly after event start
        const eventStart = new Date(f.start_datetime);
        const eventEnd = new Date(f.end_datetime);
        if (eventEnd <= eventStart) {
            toast.error(t("organizer.error_event_end_before_start"));
            return;
        }
        if (f.ticket_types.length === 0 || !f.ticket_types[0].name) {
            toast.error(t("organizer.error_ticket_type_required"));
            return;
        }
        // Each ticket type's sale_end must be after sale_start (when both provided)
        for (let i = 0; i < f.ticket_types.length; i++) {
            const tt = f.ticket_types[i];
            if (tt.sale_start_datetime && tt.sale_end_datetime) {
                const ss = new Date(tt.sale_start_datetime);
                const se = new Date(tt.sale_end_datetime);
                if (se <= ss) {
                    toast.error(t("organizer.error_ticket_sale_end_before_start", { name: tt.name || `#${i + 1}` }));
                    return;
                }
            }
        }
        setSubmitting(true);
        try {
            const body = {
                ...f,
                start_datetime: new Date(f.start_datetime).toISOString(),
                end_datetime: new Date(f.end_datetime).toISOString(),
                ticket_types: f.ticket_types.map((tt) => ({
                    ...tt,
                    price: Number(tt.price),
                    total_quantity: Number(tt.total_quantity),
                    min_per_order: Number(tt.min_per_order),
                    max_per_order: Number(tt.max_per_order),
                    sale_start_datetime: tt.sale_start_datetime ? new Date(tt.sale_start_datetime).toISOString() : null,
                    sale_end_datetime: tt.sale_end_datetime ? new Date(tt.sale_end_datetime).toISOString() : null,
                })),
            };
            if (mode === "edit") {
                await api.put(`/api/organizer/events/${eventId}`, body);
                toast.success(t("organizer.event_updated"));
                navigate(`/organizer/events/${eventId}/manage`);
            } else {
                const { data } = await api.post("/api/organizer/events", body);
                toast.success(t("organizer.event_created"));
                navigate(`/organizer/events/${data.id}/manage`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("common.error"));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-10 text-muted-foreground">{t("common.loading")}</div>;

    return (
        <div data-testid="event-form-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" />
                {t("organizer.back")}
            </button>
            <h1 className="font-heading font-black text-3xl md:text-4xl">
                {mode === "edit" ? t("organizer.edit_event") : t("organizer.create_event")}
            </h1>

            <div className="bento-card p-5 md:p-6 space-y-4">
                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("organizer.featured_image")}</label>
                    <div className="flex items-center gap-3">
                        {f.featured_image_url && (
                            <img src={f.featured_image_url} alt="" className="w-24 h-24 rounded-xl object-cover" />
                        )}
                        <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-image-btn" className="btn-outline flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            {t("organizer.upload")}
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="image-input" />
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("organizer.title_field")} *</label>
                    <input data-testid="event-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="input-modern" />
                </div>

                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("organizer.description")}</label>
                    <textarea data-testid="event-description" rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="input-modern !min-h-[120px]" />
                </div>

                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("organizer.category")}</label>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((c) => {
                            const active = f.category_ids.includes(c.id);
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    data-testid={`cat-${c.id}`}
                                    onClick={() =>
                                        setF({
                                            ...f,
                                            category_ids: active
                                                ? f.category_ids.filter((x) => x !== c.id)
                                                : [...f.category_ids, c.id],
                                        })
                                    }
                                    className={`badge-pill px-4 py-2 transition-colors ${active ? "bg-foreground text-background" : "bg-muted hover:bg-border"}`}
                                >
                                    {c.name}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("event.venue")} *</label>
                    <SearchableSelect
                        testId="event-venue"
                        options={venues.map((v) => ({ value: v.id, label: `${v.name} - ${v.address}` }))}
                        value={f.venue_id}
                        onValueChange={(v) => setF({ ...f, venue_id: v })}
                        placeholder={t("organizer.venue_search")}
                        searchPlaceholder={t("common.search")}
                        allowCreate
                        createLabel={t("organizer.add_venue")}
                        onCreate={createVenue}
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("organizer.start_datetime")} *</label>
                        <DateTimePicker
                            testId="event-start"
                            value={f.start_datetime}
                            onChange={(v) => setF({ ...f, start_datetime: v })}
                        />
                    </div>
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("organizer.end_datetime")} *</label>
                        <DateTimePicker
                            testId="event-end"
                            value={f.end_datetime}
                            onChange={(v) => setF({ ...f, end_datetime: v })}
                        />
                    </div>
                </div>

                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("event.lineup")}</label>
                    <SearchableSelect
                        testId="event-artists-search"
                        options={artists.filter((a) => !f.artist_ids.includes(a.id)).map((a) => ({ value: a.id, label: a.name }))}
                        value=""
                        onValueChange={(v) => setF({ ...f, artist_ids: [...f.artist_ids, v] })}
                        placeholder={t("organizer.artist_search")}
                        searchPlaceholder={t("common.search")}
                        allowCreate
                        createLabel={t("organizer.add_artist")}
                        onCreate={createArtist}
                    />
                    {f.artist_ids.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {f.artist_ids.map((id) => {
                                const a = artists.find((x) => x.id === id);
                                if (!a) return null;
                                return (
                                    <span key={id} className="badge-pill bg-muted flex items-center gap-2">
                                        {a.name}
                                        <button onClick={() => setF({ ...f, artist_ids: f.artist_ids.filter((x) => x !== id) })} className="text-muted-foreground hover:text-destructive">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <PhoneInput
                        label={t("organizer.contact_phone")}
                        phoneCode={f.contact_phone_code}
                        phone={f.contact_phone}
                        onChange={({ phone_code, phone }) => setF({ ...f, contact_phone_code: phone_code, contact_phone: phone })}
                        testPrefix="event-contact-phone"
                    />
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("organizer.contact_email")}</label>
                        <input data-testid="event-contact-email" type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} className="input-modern" />
                    </div>
                </div>
            </div>

            {/* Ticket types */}
            <div className="bento-card p-5 md:p-6 space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-bold text-lg">{t("organizer.ticket_types")}</h2>
                    <button type="button" onClick={addTicketType} data-testid="add-ticket-type" className="btn-outline inline-flex items-center gap-1 !py-2 !px-4">
                        <Plus className="h-4 w-4" />
                        {t("organizer.add_ticket_type")}
                    </button>
                </div>
                {f.ticket_types.map((tt, idx) => (
                    <div key={idx} data-testid={`ticket-type-row-${idx}`} className="p-4 rounded-xl border border-border space-y-3">
                        <div className="flex items-start gap-3">
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <I label={t("organizer.name")} v={tt.name} set={(v) => updateTicket(idx, { name: v })} tid={`tt-name-${idx}`} />
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">
                                        {t("organizer.price")} <span className="text-foreground font-bold">({currencyCode})</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            data-testid={`tt-price-${idx}`}
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={tt.price ?? ""}
                                            onChange={(e) => updateTicket(idx, { price: e.target.value.replace(/[^0-9]/g, "") })}
                                            onKeyDown={(e) => { if (e.key === "." || e.key === ",") e.preventDefault(); }}
                                            className="input-modern !py-2 !pr-16"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground pointer-events-none">
                                            {currencyCode}
                                        </span>
                                    </div>
                                </div>
                                <I label={t("organizer.quantity")} v={tt.total_quantity} set={(v) => updateTicket(idx, { total_quantity: v })} type="number" tid={`tt-qty-${idx}`} />
                                <div className="grid grid-cols-2 gap-3">
                                    <I label={t("organizer.min")} v={tt.min_per_order} set={(v) => updateTicket(idx, { min_per_order: v })} type="number" tid={`tt-min-${idx}`} />
                                    <I label={t("organizer.max")} v={tt.max_per_order} set={(v) => updateTicket(idx, { max_per_order: v })} type="number" tid={`tt-max-${idx}`} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{t("organizer.sale_start")}</label>
                                    <DateTimePicker testId={`tt-sstart-${idx}`} value={tt.sale_start_datetime} onChange={(v) => updateTicket(idx, { sale_start_datetime: v })} />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{t("organizer.sale_end")}</label>
                                    <DateTimePicker testId={`tt-send-${idx}`} value={tt.sale_end_datetime} onChange={(v) => updateTicket(idx, { sale_end_datetime: v })} />
                                </div>
                                <div className="sm:col-span-2">
                                    <I label={t("organizer.description")} v={tt.description} set={(v) => updateTicket(idx, { description: v })} tid={`tt-desc-${idx}`} />
                                </div>
                                {tt.sold_count > 0 && (
                                    <div className="sm:col-span-2 text-xs text-muted-foreground">
                                        {tt.sold_count} sold - quantity cannot be reduced below this number.
                                    </div>
                                )}
                            </div>
                            {!tt.id && (
                                <button type="button" onClick={() => removeTicketType(idx)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <button data-testid="event-submit" onClick={save} disabled={submitting} className="btn-primary w-full md:w-auto">
                {submitting ? t("common.loading") : mode === "edit" ? t("organizer.save") : t("organizer.create")}
            </button>

            <VenueDialog
                open={venueDialogOpen}
                onOpenChange={setVenueDialogOpen}
                countryId={effectiveCountryId}
                isAdmin={isAdmin}
                initialName={venueDialogPrefill}
                onCreated={onVenueCreated}
            />
        </div>
    );
}

const I = ({ label, v, set, type = "text", tid }) => (
    <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">{label}</label>
        <input data-testid={tid} type={type} value={v ?? ""} onChange={(e) => set(e.target.value)} className="input-modern !py-2" />
    </div>
);
